/**
 * Map HTML attribute names to their React prop equivalents. Anything not in the table
 * (including `data-*` and `aria-*`) passes through unchanged. `on*` event-handler attributes
 * are dropped entirely — host-page templates cannot install inline event handlers.
 *
 * The `style` attribute is special: HTML carries it as a string like `padding: 8px; color: red;`
 * but React requires an object like `{ padding: "8px", color: "red" }`. The string is parsed
 * here so embedders can write the natural HTML form in their `<template>` blocks.
 */
const ATTR_MAP: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  tabindex: "tabIndex",
  readonly: "readOnly",
  maxlength: "maxLength",
  minlength: "minLength",
  rowspan: "rowSpan",
  colspan: "colSpan",
  usemap: "useMap",
  frameborder: "frameBorder",
  contenteditable: "contentEditable",
  crossorigin: "crossOrigin",
  autoplay: "autoPlay",
  autocomplete: "autoComplete",
  autofocus: "autoFocus",
  spellcheck: "spellCheck",
  enctype: "encType",
  formaction: "formAction",
  formenctype: "formEncType",
  formmethod: "formMethod",
  formnovalidate: "formNoValidate",
  formtarget: "formTarget",
  novalidate: "noValidate",
  srcset: "srcSet",
  srcdoc: "srcDoc",
  inputmode: "inputMode",
  playsinline: "playsInline",
  referrerpolicy: "referrerPolicy",
  autocapitalize: "autoCapitalize",
  "accept-charset": "acceptCharset",
  "http-equiv": "httpEquiv",
};

export interface AttrMappingResult {
  /**
   * Props are mostly strings (HTML attributes are stringly-typed) except for the parsed
   * `style` object, which React requires as `Record<string, string>` rather than the raw
   * CSS text. Typed as `unknown` so consumers spread it into `React.createElement` without
   * fighting the type system.
   */
  props: Record<string, unknown>;
  droppedHandlers: string[];
}

export const htmlAttrsToReactProps = (attrs: NamedNodeMap): AttrMappingResult => {
  const props: Record<string, unknown> = {};
  const droppedHandlers: string[] = [];

  for (let i = 0; i < attrs.length; i++) {
    const attr = attrs[i];
    const name = attr.name;

    if (name.startsWith("on")) {
      droppedHandlers.push(name);
      continue;
    }

    if (name === "style") {
      props.style = parseStyleAttribute(attr.value);
      continue;
    }

    const mapped = ATTR_MAP[name] ?? name;
    props[mapped] = attr.value;
  }

  return { props, droppedHandlers };
};

/**
 * Parse a CSS text string (e.g. `padding: 8px; background: #fff;`) into the React
 * style-prop shape (`{ padding: "8px", background: "#fff" }`).
 *
 * - Property names are converted to camelCase, with two exceptions:
 *   - `--*` custom properties stay kebab-case so CSS variables work via the style prop.
 *   - The `-ms-*` vendor prefix becomes `ms*` (lowercase m); every other vendor prefix
 *     becomes `Webkit*` / `Moz*` / `O*` (capital first letter), matching React's rules.
 * - Malformed declarations (missing `:` or empty property) are skipped silently —
 *   browsers tolerate them and we follow suit.
 */
const parseStyleAttribute = (cssText: string): Record<string, string> => {
  const style: Record<string, string> = {};
  if (!cssText) return style;

  // Strip CSS block comments (`/* ... */`) before splitting. They're rare in
  // inline style but legal HTML; without stripping, a comment containing
  // a `;` or `:` would confuse the splitter. Non-greedy `*?` matches each
  // comment independently; an unclosed `/*` with no closing `*/` won't
  // match the pattern and passes through unchanged — the malformed
  // declaration that follows is then dropped by the `:`-presence guard
  // below.
  const stripped = cssText.replace(/\/\*[\s\S]*?\*\//g, "");

  for (const declaration of stripped.split(";")) {
    const trimmed = declaration.trim();
    if (!trimmed) continue;

    const colonIdx = trimmed.indexOf(":");
    if (colonIdx === -1) continue;

    const rawName = trimmed.slice(0, colonIdx).trim();
    const value = trimmed.slice(colonIdx + 1).trim();
    if (!rawName) continue;

    style[cssPropertyToReactKey(rawName)] = value;
  }

  return style;
};

const cssPropertyToReactKey = (name: string): string => {
  // CSS custom properties: pass through as-is — React supports them on the style prop verbatim.
  if (name.startsWith("--")) return name;

  // -ms-* is the only vendor prefix that stays lowercase in React (`msTransform`, not
  // `MsTransform`). Every other dash-prefix becomes capitalized (`WebkitTransform`).
  // Re-prepend a single dash to the suffix so kebabToCamel's leading-dash rule
  // capitalizes the first letter (`flex` → `Flex` → `msFlex`).
  if (name.startsWith("-ms-")) {
    return "ms" + kebabToCamel("-" + name.slice(4));
  }

  return kebabToCamel(name);
};

const kebabToCamel = (name: string): string => {
  // Leading dash → next letter capitalized (vendor prefix path).
  const normalized = name.replace(/^-([a-z])/, (_, letter: string) =>
    letter.toUpperCase(),
  );
  // Remaining `-x` segments → camelCase.
  return normalized.replace(/-([a-z])/g, (_, letter: string) =>
    letter.toUpperCase(),
  );
};
